import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    makeStyles,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@material-ui/core"
import { useSnackbar } from "../../utils/notificacoes"
import { useEffect, useRef, useState } from "react"
import { Redirect, useHistory } from "react-router-dom"
import { buscarTokenWhatsappOficial } from "../../services/comum"
import {
    createWhatsappMessageTemplate,
    maxPlaceholderIndexInText,
} from "../../services/whatsappGraphTemplate"
import {
    buildWhatsappSessionContextKey,
    clearWhatsappOfficialSession,
    loadWhatsappOfficialSession,
    saveWhatsappOfficialSession,
} from "../../utils/whatsappOfficialSession"
import useAppStore from "../../stores/AppStore"
import Cabecalho from "../Cabecalho"
import TemplateWhatsappNav from "../../components/TemplateWhatsappNav"

const IDIOMAS_COMUNS = [
    { code: "pt_BR", label: "Português (Brasil)" },
    { code: "en_US", label: "English (US)" },
    { code: "es", label: "Español" },
    { code: "en", label: "English" },
]

const useStyles = makeStyles(theme => ({
    root: {
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        paddingTop: theme.spacing(10),
        padding: theme.spacing(2),
        paddingBottom: theme.spacing(4),
        marginLeft: "var(--drawer-width, 80px)",
        width: "calc(100% - var(--drawer-width, 80px))",
        transition: "margin-left 0.2s ease, width 0.2s ease",
        boxSizing: "border-box",
        [theme.breakpoints.down("md")]: {
            marginLeft: 0,
            width: "100%",
        },
    },
    paper: {
        maxWidth: 720,
        margin: "0 auto",
        padding: theme.spacing(3),
    },
    field: {
        marginBottom: theme.spacing(2),
        width: "100%",
    },
    hint: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: "0.8125rem",
    },
    actions: {
        marginTop: theme.spacing(2),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    credBox: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: "#fff",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        "& code": {
            fontSize: "0.9rem",
            wordBreak: "break-all",
        },
    },
}))

/**
 * @param {string} raw
 * @param {number} expectedCount
 * @returns {string[] | null}
 */
function parsePipeExamples(raw, expectedCount) {
    if (expectedCount <= 0) return []
    const s = String(raw || "").trim()
    if (!s) return null
    const parts = s.split("|").map(p => p.trim())
    if (parts.length !== expectedCount) return null
    return parts
}

function buildCreateTemplatePayload({
    name,
    language,
    category,
    allowCategoryChange,
    bodyText,
    bodyExamplesRaw,
    includeHeader,
    headerText,
    headerExampleRaw,
    footerText,
    quickReply1,
    quickReply2,
    quickReply3,
}) {
    const components = []

    if (includeHeader) {
        const ht = String(headerText || "").trim()
        if (!ht) {
            throw new Error("Preencha o texto do cabeçalho ou desmarque “Incluir cabeçalho de texto”.")
        }
        const headerComp = {
            type: "HEADER",
            format: "TEXT",
            text: ht,
        }
        const hn = maxPlaceholderIndexInText(ht)
        if (hn > 0) {
            const ex = parsePipeExamples(headerExampleRaw, hn)
            if (!ex)
                throw new Error(
                    `No cabeçalho há ${hn} variável(is) ({{1}}…). Informe ${hn} exemplo(s) separados por |.`
                )
            headerComp.example = { header_text: ex }
        }
        components.push(headerComp)
    }

    const bt = String(bodyText || "").trim()
    if (!bt) throw new Error("O texto do corpo é obrigatório.")

    const bodyComp = {
        type: "BODY",
        text: bt,
    }
    const bn = maxPlaceholderIndexInText(bt)
    if (bn > 0) {
        const ex = parsePipeExamples(bodyExamplesRaw, bn)
        if (!ex)
            throw new Error(
                `No corpo há ${bn} variável(is). Informe ${bn} exemplo(s) separados por | (na ordem {{1}}, {{2}}, …).`
            )
        bodyComp.example = { body_text: [ex] }
    }
    components.push(bodyComp)

    const ft = String(footerText || "").trim()
    if (ft) {
        components.push({ type: "FOOTER", text: ft })
    }

    const quick = [quickReply1, quickReply2, quickReply3]
        .map(s => String(s || "").trim())
        .filter(Boolean)
    if (quick.length > 0) {
        components.push({
            type: "BUTTONS",
            buttons: quick.map(text => ({ type: "QUICK_REPLY", text })),
        })
    }

    return {
        name: String(name || "")
            .trim()
            .toLowerCase(),
        language: String(language || "").trim(),
        category,
        allow_category_change: allowCategoryChange,
        components,
    }
}

export default function CriarTemplateWhatsapp() {
    const classes = useStyles()
    const history = useHistory()
    const { enqueueSnackbar } = useSnackbar()
    const configuracoes = useAppStore(s => s.configuracoes)
    const userId = configuracoes?.user?.id
    const perfil = configuracoes?.user?.perfil
    const perfilPodeWhatsappTemplates =
        perfil === "CLIENTEFIEL" || perfil === "WHATSAPP"
    const idEstabelecimento = configuracoes?.user?.parametros?.estabelecimento?.id

    const tokenRef = useRef(null)
    const [wabaidGraph, setWabaidGraph] = useState("")
    const [contaWaba, setContaWaba] = useState("")
    const [telefoneWhatsapp, setTelefoneWhatsapp] = useState("")
    const [carregandoCredenciais, setCarregandoCredenciais] = useState(false)
    const [temSessao, setTemSessao] = useState(false)

    const [templateName, setTemplateName] = useState("")
    const [language, setLanguage] = useState("pt_BR")
    const [category, setCategory] = useState("UTILITY")
    const [allowCategoryChange, setAllowCategoryChange] = useState(true)
    const [bodyText, setBodyText] = useState("")
    const [bodyExamples, setBodyExamples] = useState("")
    const [includeHeader, setIncludeHeader] = useState(false)
    const [headerText, setHeaderText] = useState("")
    const [headerExamples, setHeaderExamples] = useState("")
    const [footerText, setFooterText] = useState("")
    const [quickReply1, setQuickReply1] = useState("")
    const [quickReply2, setQuickReply2] = useState("")
    const [quickReply3, setQuickReply3] = useState("")
    const [enviando, setEnviando] = useState(false)

    useEffect(() => {
        if (!userId || !perfilPodeWhatsappTemplates) return
        if (!idEstabelecimento) return
        const chave = localStorage.getItem("chaveSistema")
        if (!chave) return

        let cancelled = false
        ;(async () => {
            const fromSession = await loadWhatsappOfficialSession(
                buildWhatsappSessionContextKey(chave, userId, idEstabelecimento)
            )
            if (cancelled) return
            if (fromSession) {
                tokenRef.current = fromSession.token
                setWabaidGraph(fromSession.wabaid)
                setContaWaba(fromSession.conta || "")
                setTelefoneWhatsapp(fromSession.telefone || "")
                setTemSessao(true)
                return
            }
            setCarregandoCredenciais(true)
            const res = await buscarTokenWhatsappOficial(idEstabelecimento, chave)
            setCarregandoCredenciais(false)
            if (cancelled) return
            if (res.ok) {
                await saveWhatsappOfficialSession({
                    token: res.token,
                    wabaid: res.wabaid,
                    conta: res.conta || "",
                    telefone: res.telefone || "",
                    contextKey: buildWhatsappSessionContextKey(chave, userId, idEstabelecimento),
                })
                tokenRef.current = res.token
                setWabaidGraph(res.wabaid)
                setContaWaba(res.conta || "")
                setTelefoneWhatsapp(res.telefone || "")
                setTemSessao(true)
            } else {
                enqueueSnackbar(res.mensagem, { variant: "warning" })
                setTemSessao(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [userId, perfilPodeWhatsappTemplates, idEstabelecimento, enqueueSnackbar])

    if (userId && !perfilPodeWhatsappTemplates) {
        return <Redirect to="/servicos" />
    }

    if (!userId) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        )
    }

    const desconectar = () => {
        try {
            tokenRef.current = null
            clearWhatsappOfficialSession()
            localStorage.removeItem("chaveSistema")
            localStorage.removeItem("dataUltimoServico")
            window.name = ""
            history.push("/login")
        } catch (e) {
            console.error(e)
        }
    }

    const resolverWabaAccountId = () => {
        const fromConta = String(contaWaba || "").trim()
        if (fromConta) return fromConta
        const env = String(process.env.REACT_APP_WHATSAPP_GRAPH_WABA_ACCOUNT_ID || "").trim()
        if (env) return env
        return ""
    }

    const handleCriar = async () => {
        const nomeNorm = String(templateName || "")
            .trim()
            .toLowerCase()
        if (!/^[a-z0-9_]+$/.test(nomeNorm)) {
            enqueueSnackbar(
                "Nome do template: apenas letras minúsculas, números e sublinhado (_).",
                { variant: "error" }
            )
            return
        }

        let token = tokenRef.current != null ? String(tokenRef.current).trim() : ""
        if (!token) {
            const chaveAtual = localStorage.getItem("chaveSistema")
            const sess = await loadWhatsappOfficialSession(
                buildWhatsappSessionContextKey(chaveAtual, userId, idEstabelecimento)
            )
            if (sess?.token) {
                tokenRef.current = sess.token
                token = sess.token.trim()
                if (sess.conta) setContaWaba(sess.conta)
                if (sess.telefone) setTelefoneWhatsapp(sess.telefone)
            }
        }

        if (!token) {
            enqueueSnackbar(
                "Sem token Graph. Recarregue a página ou verifique a configuração no servidor.",
                { variant: "error" }
            )
            return
        }

        const wabaId = resolverWabaAccountId()
        if (!wabaId) {
            enqueueSnackbar(
                "ID da conta WhatsApp Business (campo conta) ausente na configuração. Verifique o endpoint comum/whatsapp/oficial ou defina REACT_APP_WHATSAPP_GRAPH_WABA_ACCOUNT_ID.",
                { variant: "error" }
            )
            return
        }

        let payload
        try {
            payload = buildCreateTemplatePayload({
                name: nomeNorm,
                language,
                category,
                allowCategoryChange,
                bodyText,
                bodyExamplesRaw: bodyExamples,
                includeHeader,
                headerText,
                headerExampleRaw: headerExamples,
                footerText,
                quickReply1,
                quickReply2,
                quickReply3,
            })
        } catch (err) {
            enqueueSnackbar(err.message || String(err), { variant: "error" })
            return
        }

        setEnviando(true)
        const result = await createWhatsappMessageTemplate(wabaId, token, payload)
        setEnviando(false)

        if (result.ok) {
            const id = result.data?.id
            const st = result.data?.status
            enqueueSnackbar(
                id
                    ? `Template criado (status: ${st || "—"}). ID: ${id}`
                    : "Template enviado para análise.",
                { variant: "success" }
            )
        } else {
            const msg =
                result.data?.error?.message ||
                result.data?.error?.error_user_msg ||
                (typeof result.data === "string" ? result.data : null) ||
                `Erro ${result.status}`
            enqueueSnackbar(msg, { variant: "error" })
            console.warn("createWhatsappMessageTemplate", result.status, result.data)
        }
    }

    const bodyVars = maxPlaceholderIndexInText(bodyText)
    const headerVars = includeHeader ? maxPlaceholderIndexInText(headerText) : 0

    return (
        <div className={classes.root}>
            <Cabecalho pagina={"Criar template WhatsApp"} cor={"#464646"} desconectar={desconectar} />

            <TemplateWhatsappNav ativo="criar" />

            <Paper className={classes.paper} elevation={2}>
                <Typography variant="h6" gutterBottom>
                    Novo template (Cloud API / Business Management)
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Criação via{" "}
                    <code>POST /`{"{"}WhatsApp-Business-Account-ID{"}"}`/message_templates</code>. O
                    token fica criptografado na sessão; não é exibido.
                </Typography>

                <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                    {carregandoCredenciais && <CircularProgress size={24} />}
                    <Typography variant="body2" color="textSecondary">
                        {temSessao ? "Sessão ativa (token criptografado)." : "Sem sessão."}
                    </Typography>
                </Box>

                <Box className={classes.credBox}>
                    <Typography variant="subtitle2" gutterBottom>
                        Dados da configuração WhatsApp (API interna)
                    </Typography>
                    <Typography variant="body2" component="div" gutterBottom>
                        <strong>Conta</strong> — <code>{contaWaba || "—"}</code>
                    </Typography>
                    <Typography variant="body2" component="div" gutterBottom>
                        <strong>wabaid</strong> — <code>{wabaidGraph || "—"}</code>
                    </Typography>
                    <Typography variant="body2" component="div">
                        <strong>Telefone</strong> — <code>{telefoneWhatsapp || "—"}</code>
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                        ID efetivo em <code>POST …/message_templates</code>:{" "}
                        <code>{resolverWabaAccountId() || "—"}</code>
                        {!(contaWaba || "").trim() &&
                        String(process.env.REACT_APP_WHATSAPP_GRAPH_WABA_ACCOUNT_ID || "").trim()
                            ? " (via REACT_APP_WHATSAPP_GRAPH_WABA_ACCOUNT_ID)"
                            : ""}
                    </Typography>
                </Box>

                <TextField
                    className={classes.field}
                    label="Nome do template (único, minúsculas, números, _)"
                    variant="outlined"
                    fullWidth
                    value={templateName}
                    onChange={e => setTemplateName(e.target.value)}
                    disabled={enviando}
                    placeholder="ex.: pedido_pronto_cliente"
                />

                <FormControl variant="outlined" className={classes.field} disabled={enviando}>
                    <InputLabel>Idioma</InputLabel>
                    <Select
                        native={false}
                        label="Idioma"
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                    >
                        {IDIOMAS_COMUNS.map(opt => (
                            <MenuItem key={opt.code} value={opt.code}>
                                {opt.label} ({opt.code})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl variant="outlined" className={classes.field} disabled={enviando}>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                        label="Categoria"
                        value={category}
                        onChange={e => setCategory(e.target.value)}
                    >
                        <MenuItem value="UTILITY">UTILITY</MenuItem>
                        <MenuItem value="MARKETING">MARKETING</MenuItem>
                        <MenuItem value="AUTHENTICATION">AUTHENTICATION</MenuItem>
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={allowCategoryChange}
                            onChange={e => setAllowCategoryChange(e.target.checked)}
                            color="primary"
                            disabled={enviando}
                        />
                    }
                    label="Permitir que a Meta ajuste a categoria (allow_category_change)"
                />
                <Typography className={classes.hint} component="div" paragraph>
                    Recomendado para reduzir rejeição por miscategorização. Templates{" "}
                    <strong>AUTHENTICATION</strong> têm regras extras de corpo/botões na documentação
                    da Meta.
                </Typography>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeHeader}
                            onChange={e => setIncludeHeader(e.target.checked)}
                            color="primary"
                            disabled={enviando}
                        />
                    }
                    label="Incluir cabeçalho de texto (HEADER / TEXT)"
                />
                {includeHeader && (
                    <>
                        <TextField
                            className={classes.field}
                            label="Texto do cabeçalho (use {{1}} para variáveis)"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={2}
                            value={headerText}
                            onChange={e => setHeaderText(e.target.value)}
                            disabled={enviando}
                        />
                        {headerVars > 0 && (
                            <TextField
                                className={classes.field}
                                label={`Exemplos do cabeçalho (${headerVars} valor(es), separados por |)`}
                                variant="outlined"
                                fullWidth
                                value={headerExamples}
                                onChange={e => setHeaderExamples(e.target.value)}
                                disabled={enviando}
                                placeholder="ex.: Promo de verão"
                            />
                        )}
                    </>
                )}

                <TextField
                    className={classes.field}
                    label="Texto do corpo (BODY) — obrigatório"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={5}
                    value={bodyText}
                    onChange={e => setBodyText(e.target.value)}
                    disabled={enviando}
                    placeholder='Olá {{1}}, seu pedido {{2}} está pronto.'
                />
                {bodyVars > 0 && (
                    <TextField
                        className={classes.field}
                        label={`Exemplos do corpo (${bodyVars} valor(es), ordem {{1}}…{{n}}, separados por |)`}
                        variant="outlined"
                        fullWidth
                        value={bodyExamples}
                        onChange={e => setBodyExamples(e.target.value)}
                        disabled={enviando}
                        placeholder="Maria|#12345"
                    />
                )}

                <TextField
                    className={classes.field}
                    label="Rodapé (FOOTER) — opcional"
                    variant="outlined"
                    fullWidth
                    value={footerText}
                    onChange={e => setFooterText(e.target.value)}
                    disabled={enviando}
                />

                <Typography variant="subtitle2">Botões QUICK_REPLY (opcional, até 3)</Typography>
                <TextField
                    className={classes.field}
                    label="Botão 1"
                    variant="outlined"
                    fullWidth
                    value={quickReply1}
                    onChange={e => setQuickReply1(e.target.value)}
                    disabled={enviando}
                />
                <TextField
                    className={classes.field}
                    label="Botão 2"
                    variant="outlined"
                    fullWidth
                    value={quickReply2}
                    onChange={e => setQuickReply2(e.target.value)}
                    disabled={enviando}
                />
                <TextField
                    className={classes.field}
                    label="Botão 3"
                    variant="outlined"
                    fullWidth
                    value={quickReply3}
                    onChange={e => setQuickReply3(e.target.value)}
                    disabled={enviando}
                />

                <Box className={classes.actions}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCriar}
                        disabled={enviando || !temSessao}
                    >
                        Criar template
                    </Button>
                    {enviando && <CircularProgress size={24} />}
                </Box>

                <Typography className={classes.hint} component="div">
                    É necessária permissão <code>whatsapp_business_management</code> no token. Variáveis
                    posicionais usam <code>{`{{1}}`}</code>, <code>{`{{2}}`}</code>, … Exemplos são
                    obrigatórios para aprovação quando há variáveis.
                </Typography>
            </Paper>
        </div>
    )
}
